import React from "react";
import { Box, Typography, Chip, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GroceryIcon from "./assets/grocery.svg";
import SprintIcon from "./assets/sprint.svg";
import AlarmWaitIcon from "./assets/alarm_wait.svg";

// Staggered fade-in animation
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Accent colors for the design
const getAccentColors = (isDark) => ({
  primary: isDark ? "#BB86FC" : "#6366f1",
  secondary: isDark ? "#03DAC6" : "#8b5cf6",
  glow: isDark ? "rgba(187, 134, 252, 0.15)" : "rgba(99, 102, 241, 0.1)",
  glowIntense: isDark ? "rgba(187, 134, 252, 0.25)" : "rgba(99, 102, 241, 0.15)",
  saved: isDark ? "#03DAC6" : "#10b981",
});

function formatTime(minutes) {
  if (!minutes || minutes === 0) return "0m";
  if (minutes <= 59) {
    return `${minutes}m`;
  } else {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h${mins}m`;
    }
  }
}

/**
 * Saved badge component - renders different variants
 */
function SavedBadge({ variant, savedColor }) {
  if (variant === "corner") {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          backgroundColor: savedColor,
          borderRadius: "50%",
          width: 22,
          height: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          zIndex: 2,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 16, color: "white" }} />
      </Box>
    );
  }

  if (variant === "ribbon") {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 80,
          height: 80,
          overflow: "hidden",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: -28,
            backgroundColor: savedColor,
            color: "white",
            fontSize: "0.6rem",
            fontWeight: 600,
            textTransform: "uppercase",
            padding: "4px 30px",
            transform: "rotate(45deg)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          Saved
        </Box>
      </Box>
    );
  }

  return null;
}

/**
 * Shared RecipeCard component for both Reciply and Web results
 * 
 * @param {Object} props
 * @param {Object} props.recipe - Recipe data with title, ingredients, time, inactive_time, image_url
 * @param {number} props.index - Index for staggered animation
 * @param {boolean} props.isSelected - Whether card is selected (shows gradient border)
 * @param {boolean} props.isOpen - Whether card is expanded
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.swipeHandlers - Swipe handlers from useSwipeable (Reciply only)
 * @param {boolean} props.showActions - Whether quick actions are visible (Reciply only)
 * @param {boolean} props.cardMovingOver - Whether card is moving over for hint (Reciply only)
 * @param {React.ReactNode} props.quickActions - Quick actions component (Reciply only)
 * @param {React.ReactNode} props.hintOverlay - Hint overlay component (Reciply only)
 * @param {React.ReactNode} props.swipeIcon - Swipe icon component (Reciply only)
 * @param {Object} props.source - Source info { name, type: 'book' | 'web', isTrusted }
 * @param {Object} props.saved - Saved state { isSaved, variant: 'chip' | 'corner' | 'ribbon' }
 * @param {boolean} props.disableHover - Disable hover effects (for preview panel)
 * @param {React.ReactNode} props.children - Expanded content
 * @param {Function} props.onImageLoad - Callback when image loads
 */
function RecipeCard({
  recipe,
  index = 0,
  isSelected = false,
  isOpen = false,
  onClick,
  swipeHandlers = {},
  showActions = false,
  cardMovingOver = false,
  quickActions,
  hintOverlay,
  swipeIcon,
  source = { name: "", type: "book", isTrusted: false },
  saved = { isSaved: false, variant: "chip" },
  disableHover = false,
  children,
  onImageLoad,
  onViewOriginal,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = getAccentColors(isDark);

  // Dynamic background with subtle gradient
  const cardBgGradient = isDark
    ? "linear-gradient(135deg, rgba(30, 30, 35, 1) 0%, rgba(20, 20, 25, 1) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)";

  // Border styling
  const gradientBorder = `linear-gradient(135deg, ${accent.primary}, ${accent.secondary})`;
  const savedBorder = saved.isSaved ? accent.saved : null;
  const normalBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  // Determine border based on state
  const getBorderBackground = () => {
    if (isSelected) return gradientBorder;
    if (saved.isSaved) return savedBorder;
    return normalBorder;
  };

  // Determine box shadow based on state
  const getBoxShadow = () => {
    if (isSelected) {
      return `0 8px 32px -8px rgba(0,0,0,${isDark ? "0.5" : "0.15"})`;
    }
    if (saved.isSaved) {
      return `0 0 0 2px ${isDark ? "rgba(3, 218, 198, 0.15)" : "rgba(16, 185, 129, 0.1)"}, 0 4px 16px -4px rgba(0,0,0,${isDark ? "0.3" : "0.1"})`;
    }
    return `0 2px 12px -4px rgba(0,0,0,${isDark ? "0.3" : "0.08"})`;
  };

  const MAX_LINES = 5;

  // Preview panel mode (disableHover is true) - card should fill container
  const isPreviewPanel = disableHover;

  return (
    <Box
      key={recipe.id}
      {...swipeHandlers}
      sx={{
        // Outer wrapper becomes the "border" via background + padding
        background: getBorderBackground(),
        padding: "2px",
        borderRadius: "18px",
        mb: isPreviewPanel ? 0 : 1.5,
        cursor: "pointer",
        position: "relative",
        touchAction: "pan-y",
        boxShadow: getBoxShadow(),
        // Animation - skip for preview panel
        ...(isPreviewPanel ? {} : {
          animation: `${fadeInUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
          animationDelay: `${Math.min(index, 10) * 0.05}s`,
          opacity: 0,
        }),
        transition:
          "box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // Fill container for preview panel
        ...(isPreviewPanel ? {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        } : {}),
        "&:hover":
          !isSelected && !disableHover
            ? {
                background: saved.isSaved
                  ? savedBorder
                  : isDark
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(0,0,0,0.12)",
                boxShadow: `0 4px 20px -4px rgba(0,0,0,${isDark ? "0.4" : "0.12"})`,
                transform: "translateY(-2px)",
              }
            : {},
      }}
      onClick={onClick}
    >
      {/* Saved badge for corner and ribbon variants */}
      {saved.isSaved &&
        (saved.variant === "corner" || saved.variant === "ribbon") && (
          <SavedBadge variant={saved.variant} savedColor={accent.saved} />
        )}

      {/* Inner card with content background */}
      <div
        style={{
          background: cardBgGradient,
          borderRadius: "16px",
          overflow: "hidden",
          ...(isPreviewPanel ? {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          } : {}),
        }}
      >
        {/* Content Wrapper - handles swipe animation */}
        <div
          className="recipe-content"
          style={{
            display: "flex",
            flexDirection: "column",
            transform:
              showActions || cardMovingOver
                ? "translateX(-208px)"
                : "translateX(0)",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            background: isSelected
              ? isDark
                ? "rgba(187, 134, 252, 0.03)"
                : "rgba(99, 102, 241, 0.02)"
              : "transparent",
            ...(isPreviewPanel ? {
              flex: 1,
              minHeight: 0,
            } : {}),
          }}
        >
          {/* Header - Title, Source, Stats, Image */}
          <div
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: ".75em",
              }}
            >
              {/* Left Column */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Title - primary, most prominent */}
                <Typography
                  variant="h6"
                  sx={{
                    margin: 0,
                    fontSize: 17,
                    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                    fontWeight: 600,
                    lineHeight: "1.4",
                    wordWrap: "break-word",
                    color: theme.palette.text.primary,
                    letterSpacing: "-0.02em",
                    textTransform: "lowercase",
                    ...(isOpen
                      ? {}
                      : {
                          display: "-webkit-box",
                          WebkitLineClamp: MAX_LINES,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                  }}
                >
                  {recipe.title?.toLowerCase() || ""}
                </Typography>

                {/* Source name - secondary */}
                <Box
                  sx={{
                    mt: "4px",
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {source.type === "book" ? (
                    // Book source - simple italic text with optional external link
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isSelected
                            ? accent.primary
                            : theme.palette.text.secondary,
                          fontSize: 12,
                          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                          fontStyle: "italic",
                          fontWeight: 400,
                          lineHeight: "1.4",
                          letterSpacing: "-0.01em",
                          opacity: isSelected ? 1 : 0.85,
                          transition: "color 0.2s ease, opacity 0.2s ease",
                          ...(isOpen
                            ? {}
                            : {
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }),
                        }}
                      >
                        <em>{source.name}</em>
                      </Typography>
                      {isPreviewPanel && onViewOriginal && (
                        <OpenInNewIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewOriginal(e);
                          }}
                          sx={{
                            fontSize: 14,
                            color: theme.palette.text.secondary,
                            opacity: 0.5,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                            "&:hover": {
                              color: accent.primary,
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    // Web source - Chip with optional trusted badge and external link
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Chip
                        label={source.name}
                        size="small"
                        icon={source.isTrusted ? <VerifiedIcon /> : undefined}
                        color={source.isTrusted ? "primary" : "default"}
                        variant={source.isTrusted ? "filled" : "outlined"}
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                          fontStyle: "italic",
                          fontWeight: 400,
                          borderRadius: "8px",
                          letterSpacing: "-0.01em",
                          background: source.isTrusted
                            ? `linear-gradient(135deg, ${accent.primary}, ${accent.secondary})`
                            : "transparent",
                          borderColor: isDark
                            ? "rgba(255,255,255,0.15)"
                            : "rgba(0,0,0,0.1)",
                          "& .MuiChip-icon": { fontSize: 12 },
                        }}
                      />
                      {isPreviewPanel && onViewOriginal && (
                        <OpenInNewIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewOriginal(e);
                          }}
                          sx={{
                            fontSize: 14,
                            color: theme.palette.text.secondary,
                            opacity: 0.5,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                            "&:hover": {
                              color: accent.primary,
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Saved chip variant (for web results) */}
                  {saved.isSaved && saved.variant === "chip" && (
                    <Chip
                      label="saved"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                        fontWeight: 500,
                        borderRadius: "8px",
                        letterSpacing: "-0.01em",
                        background: isDark
                          ? "linear-gradient(135deg, rgba(3, 218, 198, 0.2), rgba(3, 218, 198, 0.1))"
                          : "linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08))",
                        color: accent.saved,
                        border: `1px solid ${isDark ? "rgba(3, 218, 198, 0.3)" : "rgba(16, 185, 129, 0.25)"}`,
                        "& .MuiChip-icon": { fontSize: 12, color: accent.saved },
                      }}
                    />
                  )}
                </Box>

                {/* Stats bar - tertiary metadata (hidden in preview panel) */}
                {!disableHover && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 8,
                    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                  }}
                >
                  {/* Ingredients */}
                  {recipe.ingredients?.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <div style={{ width: 18, height: 18, flexShrink: 0 }}>
                        <img
                          src={GroceryIcon}
                          alt=""
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                      <div
                        style={{
                          color: theme.palette.text.secondary,
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: "20px",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {recipe.ingredients.length}
                      </div>
                    </div>
                  )}

                  {/* Active Time */}
                  {(recipe.time > 0 || recipe.active_time > 0) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <div style={{ width: 20, height: 20, flexShrink: 0 }}>
                        <img
                          src={SprintIcon}
                          alt=""
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                      <div
                        style={{
                          color: theme.palette.text.secondary,
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: "20px",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {formatTime(recipe.time || recipe.active_time)}
                      </div>
                    </div>
                  )}

                  {/* Inactive Time */}
                  {recipe.inactive_time > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <div style={{ width: 20, height: 20, flexShrink: 0 }}>
                        <img
                          src={AlarmWaitIcon}
                          alt=""
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                      <div
                        style={{
                          color: theme.palette.text.secondary,
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: "20px",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {formatTime(recipe.inactive_time)}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Right Column - Image */}
              {!isOpen && recipe.image_url && (
                <Box
                  sx={{
                    width: "75px",
                    height: "75px",
                    borderRadius: ".5em",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    flexShrink: 0,
                    marginLeft: "1em",
                  }}
                >
                  <img
                    alt={recipe.title || ""}
                    src={recipe.image_url}
                    onLoad={onImageLoad}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      transform: "scale(1.1)",
                      filter: isDark ? "brightness(0.8)" : "none",
                    }}
                  />
                </Box>
              )}
            </div>

          {/* Expanded Content - passed as children */}
          {children}
        </div>

        {/* Quick Actions (Reciply only) */}
        {quickActions}
      </div>

      {/* Hint Overlay (Reciply only) */}
      {hintOverlay}

      {/* Swipe Icon (Reciply only) */}
      {swipeIcon}
    </Box>
  );
}

export default RecipeCard;
export { fadeInUp, getAccentColors, formatTime };

